<template>
  <div class="tw:flex tw:items-center tw:justify-between tw:group">
    <div class="tw:flex tw:items-center tw:gap-2">
      <slot name="labelStart" />
      {{ label }}
    </div>
    <div
      class="tw:flex tw:font-normal"
      :class="{
        'tw:opacity-0 tw:group-hover:opacity-100': !active && !isTabbing,
      }"
      data-testid="sort-control"
    >
      <BaseButton
        :aria-label="sortButtonAriaLabel"
        class="sort-button"
        :color="active ? 'secondary' : 'form'"
        dense
        flat
        :icon="sortIcon"
        new-design
        new-icon
        @click="sortButtonClicked"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { type DesignSystemIcons } from "design-system/types";
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButton } from "shared/components/base";
import useTabbing from "shared/composables/useTabbing";
import type { Nullable } from "shared/types";

export interface Props {
  label?: string;
  sortBy: string;
}

export type ModelSortDirection = Nullable<"asc" | "desc">;

interface Model {
  sortBy: Nullable<string>;
  sortDirection: ModelSortDirection;
}

const props = withDefaults(defineProps<Props>(), {
  label: "",
});

const modelValue = defineModel<Model>({
  default: () => ({ sortBy: null, sortDirection: null }),
});

const { isTabbing } = useTabbing();

const sortButtonAriaLabel = computed<string>(() =>
  getLocaleText("table_sort_order.sort_button_label", {
    label: props.label,
  }).trim()
);

const sortIcon = computed<DesignSystemIcons>(() => {
  if (
    modelValue.value.sortBy !== props.sortBy ||
    modelValue.value.sortDirection === null ||
    modelValue.value.sortDirection === "asc"
  ) {
    return "sort-up";
  }

  return "sort-down";
});

const active = computed<boolean>(() =>
  Boolean(
    modelValue.value.sortBy === props.sortBy && modelValue.value.sortDirection
  )
);

function sortButtonClicked(): void {
  const sortDirection: ModelSortDirection =
    modelValue.value.sortBy === props.sortBy &&
    modelValue.value.sortDirection === "asc"
      ? "desc"
      : "asc";

  modelValue.value = {
    sortBy: props.sortBy,
    sortDirection,
  };
}
</script>

<style lang="scss" scoped>
.sort-button {
  --c-button-color: var(--s-color-font-default);
  --c-button-color-hover: var(--s-color-font-default);
  --c-button-bg-hover: transparent;
  --c-button-bg-active: transparent;
  --c-button-border-hover: transparent;
  --c-button-border-active: transparent;
}
</style>
